import { useEffect } from 'react';
import { NineSliceFrame } from './NineSliceFrame';
import { KeyDisplay } from './KeyDisplay';
import { useMenuNavigation } from '../../hooks/menu/useMenuNavigation';
import './ConfirmPopup.scss';

interface ConfirmPopupProps {
  message: string;
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  /**
   * When true, no navigable buttons are rendered: the popup is driven purely by
   * the CONFIRM / CANCEL shortcuts (an invisible anchor keeps CONFIRM wired).
   * The labels move to the hint line. The caller must still deactivate its own
   * navigation while the popup is open.
   */
  hideButtons?: boolean;
}

/**
 * Generic confirmation popup. Renders a modal overlay with a message and either
 * two navigable buttons (confirm / cancel, focus starts on cancel as a safe
 * default) or, with `hideButtons`, a shortcut-only prompt. Captures menu input
 * while mounted; the caller must deactivate its own navigation while open.
 */
export const ConfirmPopup = ({
  message,
  title,
  confirmLabel = 'Confirmer',
  cancelLabel = 'Annuler',
  onConfirm,
  onCancel,
  hideButtons = false,
}: ConfirmPopupProps) => {
  const { containerRef, setFocus } = useMenuNavigation({
    onConfirm: (id) => (id === 'confirm' ? onConfirm() : onCancel()),
    onCancel,
    focusClass: 'confirm-popup-focused',
  });

  useEffect(() => {
    // hideButtons → focus the invisible confirm anchor so CONFIRM validates;
    // otherwise land on the cancel button (safe default).
    setFocus(0, hideButtons ? 0 : 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="confirm-popup-overlay">
      <NineSliceFrame className="confirm-popup">
        {title && <h3 className="confirm-popup-title">{title}</h3>}
        <p className="confirm-popup-message">{message}</p>
        <div className="confirm-popup-actions" ref={containerRef}>
          {hideButtons ? (
            <span
              data-nav-row={0}
              data-nav-col={0}
              data-nav-id="confirm"
              style={{ display: 'none' }}
            />
          ) : (
            <>
              <button
                type="button"
                data-nav-row={0}
                data-nav-col={0}
                data-nav-id="confirm"
                className="confirm-popup-button"
                onClick={onConfirm}
              >
                {confirmLabel}
              </button>
              <button
                type="button"
                data-nav-row={0}
                data-nav-col={1}
                data-nav-id="cancel"
                className="confirm-popup-button"
                onClick={onCancel}
              >
                {cancelLabel}
              </button>
            </>
          )}
        </div>
        <div className="confirm-popup-hint">
          <KeyDisplay context="interface" action="CONFIRM" size="sm" />
          {hideButtons && <span>{confirmLabel}</span>}
          <KeyDisplay context="interface" action="CANCEL" size="sm" />
          {hideButtons && <span>{cancelLabel}</span>}
        </div>
      </NineSliceFrame>
    </div>
  );
};
